// BUILD-046 Owner direction of 2026-10-21 (cycle A, correction A "Phoenix midnight and tab-return refresh"): knowing WHEN the company's calendar day changes while a page
// stays open, so every date classification (Today / Yesterday / Overdue, the Overdue and Due today filters, the Overdue count, Recommended) is judged against the CURRENT
// Phoenix day and the lists are read again once the day has moved.
//
// What it does, and the two moments it acts:
//   (a) at 00:00 America/Phoenix (UTC-7 all year, no daylight saving) while the page stays open and idle: ONE timer is armed for the next Phoenix midnight (plus a small
//       margin). When it fires the watcher reads the day again; if it moved it calls `onDayChange(newDay, previousDay)` and arms the next one. A timer that fires EARLY (the day
//       has not moved) just arms again for the remainder. There is no polling.
//   (b) when the page becomes visible again (`visibilitychange` to visible, or `pageshow`), including after a device slept past midnight, when the timer may have been
//       delayed or never ran: the watcher compares the day and acts ONLY if it changed. A tab that becomes visible on the SAME Phoenix day does nothing at all (no callback).
//       Either way the timer is armed again from the clock as it is then.
// A callback that throws (or returns a rejected promise) never stops the watcher: the day is still recorded and the next midnight is still armed. stop() clears the timer and
// the visibility subscription; a stopped watcher never calls back.
//
// The watcher knows nothing about React, the DOM or the lists: the clock, the timer functions, the visibility source and the callback are all passed in, so the whole
// behavior is tested with a fake clock (phoenixDayWatcher.test.mjs). usePhoenixDay.ts binds it to the browser; MyWorkPage binds the callback to the lists controller.
//
// Pure module. Runtime import: phoenixToday of dateLabels.ts (the same Intl day the whole page uses). Erasable TypeScript only.

import { phoenixToday } from './dateLabels';

/** Milliseconds in a day. Phoenix has no daylight saving, so a Phoenix day is always exactly this long. */
export const DAY_MS = 86_400_000;
/** Added to the time left until midnight when arming, so the timer fires just AFTER the day has turned (a timer that fires a hair early is re-armed anyway). */
export const MIDNIGHT_MARGIN_MS = 100;

// `hourCycle: 'h23'` gives 00..23 (`hour12: false` can answer 24 at midnight in some engines).
const CLOCK_PARTS = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Phoenix', hourCycle: 'h23', hour: '2-digit', minute: '2-digit', second: '2-digit' });

/**
 * Milliseconds from `now` to the next 00:00:00.000 in America/Phoenix, from 1 to 86 400 000: 06:59:59.999Z is 1 ms before midnight (1), 07:00:00.000Z is midnight itself
 * so the NEXT midnight is a full day away (86 400 000). The Phoenix time of day is read with Intl (as phoenixToday reads the day); the milliseconds are the instant's own
 * (Phoenix's offset is a whole number of hours). An invalid Date gives a full day.
 */
export function msUntilNextPhoenixMidnight(now: Date): number {
  const t = now.getTime();
  if (!Number.isFinite(t)) return DAY_MS;
  const parts = CLOCK_PARTS.formatToParts(now);
  const pick = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const ms = ((t % 1000) + 1000) % 1000;
  const elapsed = ((((pick('hour') % 24) * 60 + pick('minute')) * 60 + pick('second')) * 1000) + ms;
  return DAY_MS - elapsed;
}

/** True when the Phoenix day at `now` is not `previousDay` (a YYYY-MM-DD string). */
export function dayChanged(previousDay: string, now: Date): boolean {
  return phoenixToday(now) !== previousDay;
}

/** Where the watcher learns that the page may be visible again. */
export interface VisibilitySource {
  /** True while the page is visible. */
  isVisible: () => boolean;
  /** Calls `listener` whenever the page may have become visible again (visibilitychange, pageshow). Returns the function that stops listening. */
  subscribe: (listener: () => void) => () => void;
}

export interface DayWatcherDeps {
  now: () => Date;
  setTimeout: (callback: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
  visibilitySource: VisibilitySource;
  /** Called when the Phoenix day has changed since the last time the watcher looked. May return a promise; a throw or a rejection is swallowed. */
  onDayChange: (newDay: string, previousDay: string) => void | Promise<void>;
  /** Added to the wait for the next midnight. Default MIDNIGHT_MARGIN_MS. */
  marginMs?: number;
}

export interface DayWatcher {
  /** Starts watching; returns the Phoenix day at this moment. Starting a started watcher changes nothing. */
  start: () => string;
  /** Clears the timer and stops listening. A stopped watcher never calls back. Safe to call twice. */
  stop: () => void;
  /** The Phoenix day as of the last look (the start, the last timer or the last visible-again check). */
  currentDay: () => string;
}

export function createDayWatcher(deps: DayWatcherDeps): DayWatcher {
  const margin = deps.marginMs ?? MIDNIGHT_MARGIN_MS;
  let running = false;
  let day = '';
  let timer: unknown = null;
  let unsubscribe: (() => void) | null = null;

  function disarm(): void {
    if (timer !== null) {
      deps.clearTimeout(timer);
      timer = null;
    }
  }

  function arm(): void {
    disarm();
    timer = deps.setTimeout(onTimer, Math.max(1, msUntilNextPhoenixMidnight(deps.now()) + margin));
  }

  /** Reads the day; when it moved, records it and tells the owner. The owner's failure never reaches the watcher. */
  function look(): void {
    const next = phoenixToday(deps.now());
    if (next === day) return;
    const previous = day;
    day = next;
    try {
      const result = deps.onDayChange(next, previous);
      if (result !== undefined && result !== null && typeof (result as Promise<void>).then === 'function') {
        (result as Promise<void>).then(undefined, () => undefined);
      }
    } catch {
      // a failed reload must not stop the watcher: the next midnight is armed below
    }
  }

  function onTimer(): void {
    timer = null;
    if (!running) return;
    look();
    if (running) arm();
  }

  function onVisibleAgain(): void {
    if (!running || !deps.visibilitySource.isVisible()) return;
    look();
    if (running) arm();
  }

  return {
    start() {
      if (running) return day;
      running = true;
      day = phoenixToday(deps.now());
      unsubscribe = deps.visibilitySource.subscribe(onVisibleAgain);
      arm();
      return day;
    },
    stop() {
      running = false;
      disarm();
      if (unsubscribe !== null) {
        unsubscribe();
        unsubscribe = null;
      }
    },
    currentDay: () => day,
  };
}

// ───────────────────────── the browser's visibility, behind a small seam ─────────────────────────

/** The part of `document` the visibility source uses. */
export interface DocumentLike {
  visibilityState: string;
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

/** The part of `window` the visibility source uses. */
export interface WindowLike {
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

/** A VisibilitySource over a document and a window: `visibilitychange` on the document and `pageshow` (a page restored from the back-forward cache) on the window. */
export function createDomVisibilitySource(doc: DocumentLike, win: WindowLike): VisibilitySource {
  return {
    isVisible: () => doc.visibilityState === 'visible',
    subscribe(listener) {
      doc.addEventListener('visibilitychange', listener);
      win.addEventListener('pageshow', listener);
      return () => {
        doc.removeEventListener('visibilitychange', listener);
        win.removeEventListener('pageshow', listener);
      };
    },
  };
}
